import { Injectable, signal, effect } from '@angular/core'
import { TranslateService } from '@ngx-translate/core'

/**
 * The TranslationService is responsible for managing the application's language settings.
 * It uses Angular signals to reactively update the language and persists the selected language in local storage.
 *
 * @description
 * This service initializes with a default language ('en') and checks for a saved language in local storage.
 * If a saved language is found, it updates the language signal. The service also sets up an effect to update
 * the TranslateService and local storage whenever the language signal changes.
 *
 * @usage
 * To set up translations for a component, import the TranslateModule in your component.
 * Then, use the Translate pipe in your templates to translate text based on the current language.
 *
 * @example
 * // In your component
 * import { TranslateModule } from '@ngx-translate/core';
 *
 * @Component({
 *   ...
 *   imports: [ TranslateModule...]
 * })
 * export class YourComponent {
 *   ...
 * }
 *
 * // In your template
 * <p>{{ 'HELLO' | translate }}</p>
 */
@Injectable({
    providedIn: 'root',
})
export class TranslationService {
    /**
     * Signal for the current language.
     */
    languageSignal = signal<string>('en')
    /**
     * Default language.
     */
    defaultLang = 'en'

    /**
     * Constructs a new TranslationService instance.
     *
     * Initializes the `languageSignal` from local storage, if present.
     * An effect is used to load and activate the language in `TranslateService` and persist it to local storage.
     * Only the active language file is requested (no eager fallback load of the default language).
     *
     * @param translateService The TranslateService instance.
     */
    constructor(private translateService: TranslateService) {
        const savedLang = localStorage.getItem('lang')

        if (savedLang) {
            this.languageSignal.update(() => savedLang)
        }

        effect(() => {
            this.translateService.use(this.languageSignal())
            localStorage.setItem('lang', this.languageSignal())
        })
    }
}
